import {
	Alert,
	Box,
	Icons,
	Container,
	Text,
	Inline,
	Stack,
	Heading,
	joinAttributes,
	RelativeTime,
	Time,
	Badge,
	Dropdown,
	Button,
	Component,
	Divider,
} from "@sembark-travel/ui/base"
import { Link } from "@sembark-travel/ui/router"
import {
	getDiff,
	utcTimestampToLocalDate,
} from "@sembark-travel/datetime-utils"
import React from "react"
import { ResendInvitation, DisableDeleteAccount, EnableAccount } from "./List"
import { IUser } from "./store"
import { useXHR, xhr } from "@sembark-travel/xhr"
import { Dialog, DialogHeader, useDialog } from "@sembark-travel/ui/dialog"
import {
	Form,
	GetFieldValue,
	isTruthy,
	RadioInputField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui/form"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui/copy-to-clipboard"
import * as Validator from "yup"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import {
	passwordValidator,
	passwordValidatorRegex,
} from "../ResetPassword/utils"
import { PasswordValidationCheckInfo } from "../ResetPassword/NewAndConfirmPasswordFields"
import { SettingsCard } from "../Settings"
import { TwoFactorAuth } from "../Settings/MFAs"

export function UserDetails({
	user,
	onChange,
}: {
	user: IUser
	onChange: () => void
}) {
	const roles = user.roles || []
	const { profile_image_url } = user
	return (
		<>
			<Stack gap="8" padding="4">
				<Container rounded="md" paddingY="4" backgroundColor="default">
					<Inline flexWrap="wrap" gap="4">
						<Inline
							flex={{
								xs: "1",
								md: "none",
							}}
							justifyContent={"between"}
						>
							<Box>
								{profile_image_url ? (
									<Box
										as="img"
										src={profile_image_url}
										alt="Avatar"
										style={{ width: "150px", height: "150px" }}
										borderWidth="1"
										borderColor="default"
										rounded="lg"
									/>
								) : (
									<Inline
										style={{ width: "150px", height: "150px" }}
										borderWidth="1"
										borderColor="accent"
										rounded="lg"
										bgColor="accent"
										color="accent"
										justifyContent="center"
										alignItems="center"
									>
										<Icons.User size="20" />
									</Inline>
								)}
							</Box>

							<Box
								display={{
									md: "none",
								}}
								textAlign="right"
								color="accent"
								borderColor="accent"
								backgroundColor="accent"
								width="8"
								height="8"
								borderWidth="1"
								rounded="md"
							>
								<UserActionEllipseComponent user={user} onChange={onChange} />
							</Box>
						</Inline>

						<Stack flex="1" gap="1">
							<Inline
								justifyContent="between"
								gap="4"
								flexWrap={{
									md: "nowrap",
									xs: "wrap",
								}}
							>
								<Stack gap="3">
									<Inline alignItems="center" gap="4">
										<Heading as="h3" fontSize="2xl">
											{user.name}
										</Heading>
										<Box>
											{!user.email_verified_at ? (
												<Badge warning>Pending</Badge>
											) : user.deleted_at ? (
												<Badge danger>Deleted </Badge>
											) : user.disabled_at ? (
												<Badge warning>
													<Inline alignItems="center" gap="1">
														<Icons.BankNotes />
														<Box>Disabled</Box>
													</Inline>
												</Badge>
											) : (
												<Badge success>
													<Inline alignItems="center" gap="1">
														<Icons.BankNotes />
														<Box>Active</Box>
													</Inline>
												</Badge>
											)}
										</Box>
									</Inline>
									<Box>
										{joinAttributes(
											<Box display="inlineBlock">
												<Box as="span" padding="1">
													<Icons.Mail
														color={user.email_verified_at ? "black" : "warning"}
													/>
												</Box>
												{user.email}
											</Box>,
											user.can_disable_2fa
												? "Can Disable 2FA"
												: "Can't Disable 2FA"
										)}
									</Box>
									<Box>
										{!!user.disabled_at || !!user.deleted_at ? (
											<Badge
												{...(user.disabled_at
													? { warning: true }
													: { danger: true })}
											>
												<Stack textAlign="left" gap="1">
													<Box fontSize="base" fontWeight="semibold">
														Reason for disabling
													</Box>

													<Box as="span" fontSize="base" fontWeight="normal">
														{user.deletion_category
															? user.deletion_category === "Others"
																? user.deletion_reason
																: user.deletion_category
															: user.disable_category === "Others"
																? user.disable_reason
																: user.disable_category}
													</Box>
												</Stack>
											</Badge>
										) : null}
									</Box>
								</Stack>

								<Box
									color="accent"
									borderColor="accent"
									backgroundColor="accent"
									padding="2"
									borderWidth="1"
									rounded="md"
								>
									<UserAttribute
										prop={
											<Inline
												justifyContent="between"
												gap="2"
												alignItems="center"
												color="accent"
												fontWeight="semibold"
											>
												Roles
												{!user.is_owner && !user.deleted_at ? (
													<Link to="edit-roles" title="Edit Roles">
														<Box
															color="accent"
															borderColor="accent"
															backgroundColor="accent"
															width="8"
															height="8"
															display="flex"
															justifyContent="center"
															alignItems="center"
															borderWidth="1"
															rounded="md"
														>
															<Icons.Pencil size="5" />
														</Box>
													</Link>
												) : null}
											</Inline>
										}
									>
										{!roles.length ? (
											<Box color="muted">Not Assigned</Box>
										) : (
											joinAttributes(
												...roles.map((r) => (
													<Box key={r.id} display="inlineBlock">
														{r.name}
													</Box>
												))
											)
										)}
									</UserAttribute>
								</Box>
								<Box>
									<Box
										display={{
											xs: "none",
											md: "block",
										}}
										textAlign="right"
										borderColor="muted"
										borderWidth="1"
										rounded="md"
									>
										<UserActionEllipseComponent
											user={user}
											onChange={onChange}
										/>
									</Box>
								</Box>
							</Inline>

							<Divider sm />

							<Inline gap="16" fontWeight="normal" flexWrap="wrap">
								{user.password_updated_at ? (
									<>
										<UserAttribute
											prop={
												<Inline fontWeight="normal">
													<Icons.Lock stroke="2" /> Password Last Updated
												</Inline>
											}
										>
											<Box>
												<RelativeTime timestamp={user.password_updated_at} />
											</Box>
										</UserAttribute>
										<UserAttribute
											prop={
												<Inline fontWeight="normal">
													<Icons.Security stroke="2" /> 2FA Enabled
												</Inline>
											}
										>
											{user.two_fa_enabled ? (
												<Inline
													gap={"1"}
													alignItems="center"
													fontWeight="semibold"
												>
													<Icons.OkCircleSolid color="success" size="6" /> Yes
												</Inline>
											) : (
												<Inline
													alignItems="center"
													gap={"1"}
													fontWeight="semibold"
												>
													<Icons.CancelCircleSolid color="danger" size="6" /> No
												</Inline>
											)}
										</UserAttribute>
										<UserAttribute
											prop={
												<Inline fontWeight="normal">
													<Icons.Logout /> Last Login
												</Inline>
											}
										>
											{user.last_login_activity ? (
												<RelativeTime
													value={utcTimestampToLocalDate(
														user.last_login_activity.created_at
													)}
												/>
											) : (
												"Never logged in"
											)}
										</UserAttribute>
									</>
								) : null}
								{user.email_verified_at ? (
									<UserAttribute
										prop={
											<Inline fontWeight="normal">
												<Icons.User /> User Since
											</Inline>
										}
									>
										<Time
											value={utcTimestampToLocalDate(user.email_verified_at)}
										/>
									</UserAttribute>
								) : null}
								{user.email_verified_at ? (
									<UserAttribute
										prop={
											<Inline fontWeight="normal">
												<Icons.User /> Invited By
											</Inline>
										}
									>
										{user?.created_by?.name || "-"}
									</UserAttribute>
								) : null}
							</Inline>
						</Stack>
					</Inline>
				</Container>
				{user.disabled_at ? (
					<Alert status="warning" title="Account Disabled">
						<Text>
							This account has been disabled{" "}
							<RelativeTime timestamp={user.disabled_at} /> by{" "}
							{user.disabled_by?.name || "You"}
						</Text>
					</Alert>
				) : null}
				{!user.email_verified_at &&
				getDiff(
					new Date(),
					utcTimestampToLocalDate(user.invited_at),
					"minute"
				) >= 5 ? (
					<Alert status="warning" title="Email not verified">
						<Stack gap="2">
							{user.signup_at ? (
								<Text>
									Email verification request was sent{" "}
									<RelativeTime
										value={utcTimestampToLocalDate(user.invited_at)}
									/>{" "}
									but has not been verified yet.
								</Text>
							) : (
								<Text>
									{user.name} was invited{" "}
									<RelativeTime
										value={utcTimestampToLocalDate(user.invited_at)}
									/>{" "}
									but has not verified the email address.
								</Text>
							)}
							<Box>
								<ResendInvitation user={user} onChange={() => onChange()} />
							</Box>
						</Stack>
					</Alert>
				) : null}
			</Stack>
			<Stack gap="8" padding="4">
				<SettingsCard
					iconName="EmptyShield"
					title={
						<>
							Two Factor Authentication <Icons.NewFeature size={"10"} />
						</>
					}
					description="Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to log in."
				>
					<TwoFactorAuth user={user} showDisableButton />
				</SettingsCard>
			</Stack>
		</>
	)
}

function UserAttribute({
	prop,
	children,
}: {
	prop: React.ReactNode
	children: React.ReactNode
}) {
	return (
		<Stack gap="1">
			<Box
				fontSize="sm"
				// textTransform="uppercase"
				letterSpacing="wider"
				fontWeight="semibold"
				color="muted"
				whiteSpace="preserve"
			>
				{prop}
			</Box>
			<Box fontSize="md" fontWeight="semibold">
				{children}
			</Box>
		</Stack>
	)
}

interface PasswordResetFormType {
	_method: string
	password: string
}

const resetPasswordValidation = Validator.object().shape({
	password: passwordValidator,
})
function PasswordReset({
	user,
	onSuccess,
	children,
}: {
	user: IUser
	onSuccess: () => void
	children: (props: { onOpen: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()

	return isTruthy((user.roles || []).filter((role) => role.name === "Admin")) ||
		!user.email_verified_at ? null : (
		<>
			{children({ onOpen: open })}
			<Dialog open={isOpen} onClose={close} sm>
				<Component initialState={false}>
					{({ state, setState }) => (
						<>
							<DialogHeader title="Reset Password" closeButton={!state} />
							<Form<PasswordResetFormType>
								onSubmit={withServerErrors(async (values) => {
									const payload = {
										require_password_change: isTruthy(values._method) ? 1 : 0,
										can_not_change_password: isTruthy(values._method) ? 0 : 1,
										password: values.password,
										_method: "PATCH",
									}
									const { data } = await xhr.post(
										`users/${user.uid}/reset-password`,
										payload as object
									)
									close()
									onSuccess()
									showSnackbar(
										data.message ||
											`${user.name}'s account access has been disabled.`
									)
								})}
								initialValues={{ _method: "1" }}
								subscription={{ submitting: true }}
								validate={validateFormValues(resetPasswordValidation)}
							>
								{({ submitting, handleSubmit, form }) => (
									<form noValidate onSubmit={handleSubmit}>
										<Dialog.Body>
											<Stack gap="4" justifyContent="between" flexWrap="wrap">
												<GetFieldValue<boolean> name="_method">
													{({ value }) => (
														<Inline
															rounded="md"
															overflow="hidden"
															borderWidth="1"
															collapseBelow="sm"
														>
															<Stack
																padding="4"
																bgColor={
																	isTruthy(value) ? "primary" : "default"
																}
																as="label"
																cursor="pointer"
																flex={"1"}
															>
																<RadioInputField
																	name="_method"
																	label="Require user to change password on first login"
																	help={UnorderList([
																		"Mandatory change on first login",
																		"Prevent default reuse",
																		"Improves security",
																	])}
																	value="1"
																/>
															</Stack>
															<Stack
																padding="4"
																bgColor={
																	!isTruthy(value) ? "primary" : undefined
																}
																as="label"
																cursor="pointer"
															>
																<RadioInputField
																	name="_method"
																	label="User cannot change password"
																	help={UnorderList([
																		"User can't update password",
																		"Only admin can change",
																		"Admin must secure it",
																	])}
																	value="0"
																/>
															</Stack>
														</Inline>
													)}
												</GetFieldValue>

												{state ? (
													<Inline
														alignItems={"start"}
														gap={"2"}
														justifyContent="between"
														flexWrap={"wrap"}
													>
														<GetFieldValue<
															PasswordResetFormType["password"]
														> name="password">
															{({ value }) => (
																<>
																	<Inline flex="1" alignItems="end" gap="2">
																		<TextInputField
																			label="New Password"
																			name="password"
																			type="text"
																			placeholder="Enter password"
																			autoComplete="no_please"
																		/>
																		{passwordValidatorRegex.test(value) ? (
																			<CopyToClipboard>
																				{({ copyTextToClipboard }) => (
																					<CopyToClipboardButton
																						status="primary"
																						onClick={async () => {
																							return copyTextToClipboard(
																								form.getFieldState("password")
																									?.value || ""
																							)
																						}}
																						inline
																						iconOnly
																					>
																						<Icons.Duplicate size="7" />
																					</CopyToClipboardButton>
																				)}
																			</CopyToClipboard>
																		) : null}
																	</Inline>

																	<Stack color="muted">
																		<Inline paddingY="2">
																			<Text as="span" fontSize="sm">
																				Password should be
																			</Text>
																		</Inline>

																		<PasswordValidationCheckInfo
																			password={value}
																		/>
																	</Stack>
																</>
															)}
														</GetFieldValue>
													</Inline>
												) : null}
											</Stack>
										</Dialog.Body>
										<Dialog.Footer>
											<Stack gap="4">
												<SubmissionError />
												<Inline gap="2" justifyContent="end">
													{state ? (
														<>
															{" "}
															<Button
																type={"submit"}
																level="primary"
																status="danger"
																disabled={submitting}
															>
																{submitting
																	? "Submiting... "
																	: "Reset Password"}
															</Button>
															<Button onClick={close}>Cancel</Button>
														</>
													) : (
														<Button
															type={"button"}
															level="primary"
															status="danger"
															onClick={() => {
																if (!state) {
																	setState(true)
																}
															}}
														>
															Continue
														</Button>
													)}
												</Inline>
											</Stack>
										</Dialog.Footer>
									</form>
								)}
							</Form>
						</>
					)}
				</Component>
			</Dialog>
		</>
	)
}

function UnorderList(list: string[]) {
	return (
		<Stack paddingTop="2">
			<Box listStyleType="disc" as="ul">
				{list.map((item) => (
					<Box key={item} as="li">
						{item}
					</Box>
				))}
			</Box>
		</Stack>
	)
}

function UserActionEllipseComponent({
	user,
	onChange,
}: {
	user: IUser
	onChange: () => void
}) {
	return !user.deleted_at ? (
		!!user.is_owner && user.email_verified_at ? null : (
			<Dropdown alignRight>
				<Button level="tertiary" as={Dropdown.Toggle} size="sm">
					<Icons.DotsVertical />
				</Button>
				<Dropdown.Menu>
					<Box fontSize="base">
						<Link to="edit" title="Edit Name">
							<Icons.Pencil /> Update Details
						</Link>
					</Box>

					<ResendInvitation user={user} onChange={() => onChange()}>
						{({ onResend }) => (
							<Dropdown.MenuItem onClick={onResend}>
								<Inline gap={"1"} alignItems={"center"}>
									<Icons.Mail />
									Resend Invitation
								</Inline>
							</Dropdown.MenuItem>
						)}
					</ResendInvitation>
					<DisableDeleteAccount user={user} onSuccess={() => onChange()}>
						{({ onWipe }) => (
							<Dropdown.MenuItem onClick={onWipe} color="warning">
								{!user.email_verified_at || user.disabled_at ? (
									<Inline gap="1" alignItems={"center"}>
										<Icons.Trash /> Delete Account
									</Inline>
								) : (
									<Inline gap="1" alignItems={"center"}>
										<Icons.Ban />
										Delete / Disable Account
									</Inline>
								)}
							</Dropdown.MenuItem>
						)}
					</DisableDeleteAccount>
					<EnableAccount user={user} onSuccess={() => onChange()}>
						{({ onEnable }) => (
							<Dropdown.MenuItem onClick={onEnable} color="success">
								<Icons.Refresh opacity="50" /> Enable Account
							</Dropdown.MenuItem>
						)}
					</EnableAccount>
					{!user.email_verified_at || user.disabled_at ? null : (
						<Component initialState={false}>
							{({ state, setState }) => (
								<Dropdown.MenuItem
									onClick={async () => {
										if (state) return
										if (
											!window.confirm(
												`Are you sure you want to ${
													user.password_login_disabled_at
														? "Enabled"
														: "Disable"
												} login via Password`
											)
										) {
											return
										}
										setState(true)
										try {
											await xhr.patch(`/users/${user.id}/toggle-password-login`)
											onChange()
										} catch (e) {
											const error = e as Error
											alert(
												error.message ||
													"Something went wrong. Please try after sometime."
											)
										}
										setState(false)
									}}
								>
									<Icons.FingerPrint />{" "}
									{user.password_login_disabled_at ? "Enable" : "Disable"}{" "}
									Password Login
								</Dropdown.MenuItem>
							)}
						</Component>
					)}

					<PasswordReset user={user} onSuccess={() => onChange()}>
						{({ onOpen }) => (
							<Dropdown.MenuItem onClick={onOpen}>
								<Icons.Lock /> Password Reset
							</Dropdown.MenuItem>
						)}
					</PasswordReset>
				</Dropdown.Menu>
			</Dropdown>
		)
	) : null
}
